import { appState } from "./AppState";
import { appElements } from "./Elements";
import { util, $format } from "@ax/office-shared-lib";
import { resources } from "@ax/office-cdn-app-resources";
import { TelemetryProperties } from "@ax/office-cdn-app-telemetry";
import { strStartsWith } from "@microsoft/applicationinsights-core-js";
import { ApplicationInsights, IExceptionTelemetry, IPageViewTelemetry, ITraceTelemetry, Snippet } from "@microsoft/applicationinsights-web"
import { AppStateTelemetryPlugin } from "./Telemetry/AppStateTelemetryPlugin";

declare global {
    interface Window {
        appInsights?: ApplicationInsights,
        dynamicsAppInsights?: {
            eventQueue: Array<() => void>,
            originalFunctions: Partial<ApplicationInsights>,
            initialized: boolean
        }
    }
}

// Generate the local correlation ID early
const localRunCorrelationId = util.generateGuid();
const TrimmingTelemetryPlugin = new AppStateTelemetryPlugin(localRunCorrelationId);

const EmptyConnectionString = "InstrumentationKey=00000000-0000-0000-0000-000000000000";

let appInsightsConfig: Snippet["config"] = {
    disableTelemetry: true,
    connectionString: EmptyConnectionString,
    cookieCfg: {
        enabled: false,
    },
    extensions: [TrimmingTelemetryPlugin],
    extensionConfig: {
        ["AppInsightsCfgSyncPlugin"]: {
            // Disable loading AppInsights configuration from its CDN
            cfgUrl: ""
        }
    }
};

if (sessionStorage && sessionStorage.AppInsightsKey) {
    if (strStartsWith(sessionStorage.AppInsightsKey, "InstrumentationKey")) {
        appInsightsConfig.connectionString = sessionStorage.AppInsightsKey;
        appInsightsConfig.disableTelemetry = false;
    }
}

let localAppInsights = new ApplicationInsights({
    config: appInsightsConfig
});

window.dynamicsAppInsights = {
    eventQueue: [],
    originalFunctions: {},
    initialized: false,
};
window.appInsights = localAppInsights;

export namespace telemetryLogging {

    /** The map keyed by source ID that contains the operation set ID correlating telemetry that is currently in use. */
    export const TelemetryOperationSetIdMap = new util.Map<string>();

    /** List of methods that will be stubbed when appInsights is in a pending load state */
    const appInsightsMethodsToReplace: (keyof ApplicationInsights)[] = [
        "trackEvent",
        "trackDependencyData",
        "trackException",
        "trackMetric",
        "trackPageView",
        "trackPageViewPerformance",
        "trackTrace",
        "startTrackEvent",
        "stopTrackEvent",
        "startTrackPage",
        "stopTrackPage",
        "addTelemetryInitializer",
        "setAuthenticatedUserContext",
        "clearAuthenticatedUserContext",
        "flush"
    ];

    export function createMockAppInsightsMethod(functionName: string) {
        window.dynamicsAppInsights.originalFunctions[functionName] = window.appInsights[functionName];
        window.appInsights[functionName] = function() {
            const origArguments = arguments;
            window.dynamicsAppInsights.eventQueue.push(function() {
                window.appInsights[functionName].apply(window.appInsights, origArguments);
            });
        };
    }

    export function insertDynamicsAppInsightsQueues() {
        for (const functionName of appInsightsMethodsToReplace) {
            createMockAppInsightsMethod(functionName);
        }
    }

    export function clearDynamicsAppInsightsQueues() {
        for (const functionName in window.dynamicsAppInsights.originalFunctions) {
            const realFunction = window.dynamicsAppInsights.originalFunctions[functionName];
            window.appInsights[functionName] = realFunction;
            delete window.dynamicsAppInsights.originalFunctions[functionName];
        }

        // Process the queued events
        let queuedItem = window.dynamicsAppInsights.eventQueue.shift();
        while (queuedItem !== undefined) {
            queuedItem();
            queuedItem = window.dynamicsAppInsights.eventQueue.shift();
        }
    }

    /** The correlation ID that correlates telemetry for current page load. */
    const RunCorrelationID = localRunCorrelationId;

    /**
     * Initializes the appInsights object setting appropriate build information, adding initializers, and appropriately queueing telemetry events until telemetry is properly initialized.
     */
    export function initializeAppInsights(): void {
        if (window.dynamicsOfficeBuild !== undefined) {
            window.appInsights.context.application.ver = window.dynamicsOfficeBuild.buildVersion;
            window.appInsights.context.application.build = window.dynamicsOfficeBuild.buildType;
        }

        if (window.dynamicsAppInsights.initialized !== true) {
            telemetryLogging.insertDynamicsAppInsightsQueues();
        }
    }
    window.appInsights && telemetryLogging.initializeAppInsights();

    /**
     * Initializes the app's telemetry, and replays any telemetry events that were previously queued before the telemetry was initialized.
     * @param instrumentationKey The telemetry instrumentation key to configure.
     */
    export function initializeTelemetry(instrumentationKey?: string): void {
        // Only allow connection strings provided in the instrumentation key property to be processed
        let newConfig: Snippet["config"];
        if (instrumentationKey && strStartsWith(instrumentationKey, "InstrumentationKey")) {
            newConfig = {
                connectionString: instrumentationKey,
                disableTelemetry: false,
            };
        } else {
            newConfig = {
                connectionString: EmptyConnectionString,
                disableTelemetry: true,
            };
        }

        if (newConfig) {
            // If telemetry hasn't previously been initialized, replay queued telemetry
            if (window.dynamicsAppInsights.initialized !== true && !newConfig.disableTelemetry) {
                window.appInsights.loadAppInsights();

                // Merge the new configuration
                window.appInsights.updateCfg(newConfig, true);

                telemetryLogging.clearDynamicsAppInsightsQueues();
                window.dynamicsAppInsights.initialized = true;
            } else if (window.dynamicsAppInsights.initialized === true) {
                // Merge the new configuration
                window.appInsights.updateCfg(newConfig, true);
            }
            // If not initialized and telemetry is disabled, do nothing.
        }
    }

    export interface LogEventOptions extends TelemetryProperties {

        /** The type of message. */
        MessageType?: util.MessageType,

        /** The level "severity" of the telemetry. */
        TelemetryType?: util.telemetry.Type,

        /** Tetailed telemetry data. */
        TelemetryDetails?: any,

        /** The error stack trace. */
        Stack?: any
    }

    /**
     * Logs that the provided event occurred at the current time, adjusting an attribute on the content to reflect the last time the event occurred.
     * @param event The ID of the event that completed.
     * @param options The optional parameters to adjust how events are logged.
     */
    export function logEvent(event: string, options?: LogEventOptions): void {
        options = options || {};
        if (appState.State.Features.LogTelemetry && options.MessageType === util.MessageType.Telemetry) {

            // Remove options that shouldn't be logged in telemetry
            delete options.MessageType;

            // If the telemetry details are a property bag, add each property to the telemetry object; otherwise, assume the value is a string
            // and set the details to the provided details.
            if (options.TelemetryDetails instanceof Object) {
                var telemetryDetails = options.TelemetryDetails;
                delete options.TelemetryDetails;
                for (var detailProperty in telemetryDetails) {
                    options[detailProperty] = telemetryDetails[detailProperty];
                }
            }

            // Add the telemetry properties to the telemetry event object
            var telemetryObject: any = {};
            for (var telemetryProperty in options) {
                telemetryObject[telemetryProperty] = options[telemetryProperty];
            }

            // If the telemetry is for a specific source and the source currently has an operation set ID, apply the operation set ID to the telemetry
            if (options.SourceID !== undefined && telemetryLogging.TelemetryOperationSetIdMap.containsKey(options.SourceID)) {
                telemetryObject.OperationSetID = telemetryLogging.TelemetryOperationSetIdMap.get(options.SourceID);
            }

            // Write the client UTC timestamp for appropriate telemetry based calculations
            telemetryObject.LoggedTimestamp = new Date().toISOString();

            // Default the telemetry type to info, if not specified
            options.TelemetryType = options.TelemetryType !== undefined ? options.TelemetryType : util.telemetry.Type.Informational;

            // Log the telemetry appropriately
            switch (options.TelemetryType) {
                case util.telemetry.Type.Exception:
                    var error: any = new Error(event);
                    error.stack = options.Stack;
                    const exceptionEvent :IExceptionTelemetry = {
                        exception: error
                    };
                    window.appInsights.trackException(exceptionEvent, telemetryObject);
                    window.appInsights.flush();
                    break;
                case util.telemetry.Type.Usage:
                    // User details are only logged on usage events
                    const userDetails = appState.State?.getUserDetails();
                    if (userDetails) {
                        // Ensure that user details are always in the properties of usage events
                        telemetryObject.UserID = userDetails.objectID;
                        telemetryObject.TenantID = userDetails.tenantID;
                    }
                    const pageViewEvent: IPageViewTelemetry = {
                        name: event,
                        isLoggedIn: !!userDetails,
                        properties: telemetryObject,
                    };
                    window.appInsights.trackPageView(pageViewEvent);
                    break;
                default:
                    let traceEvent: ITraceTelemetry = {
                        message: event
                    };
                    window.appInsights.trackTrace(traceEvent, telemetryObject);
                    break;
            }
        }

        if (appState.State.Features.LogDomEvents) {
            appElements.changeContentAttribute($format("data-last-{0}", telemetryLogging._qualifyBySourceID(event, options.SourceID)), new Date().getTime().toString());
        }
    }

    // Configure DOM telemetry logging
    util.telemetry.configureDomTelemetry(telemetryLogging.logEvent);

    export interface LogErrorTelemetryOptions {

        /** The severity of the error. */
        Severity: util.telemetry.ErrorSeverity;

        /** The error details to log. */
        Details?: string;

        /** Includes telemetry properties that should be logged. */
        TelemetryProperties?: any;

        /** The ID for the source of the error. */
        SourceID?: string;
    }

    /**
     * Logs the provided error message as an error telemetry event.
     * @param message The error message to log.
     * @param errorCode The error code to log.
     * @param options The error options to display.
     */
    export function logErrorTelemetry(message: string, errorCode: string, options: LogErrorTelemetryOptions): void {

        if (options.TelemetryProperties === undefined) {
            options.TelemetryProperties = {};
        }

        // Ensure a stack is always included with error telemetry
        if (options.TelemetryProperties.Stack === undefined) {
            try { throw new Error(); } catch (e) { options.TelemetryProperties.Stack = e.stack; }
        }

        // Create the base telemetry event options
        var eventOptions: any = {
            MessageType: util.MessageType.Telemetry,
            SourceID: options.SourceID,
            Severity: util.telemetry.ErrorSeverity[options.Severity] || options.Severity
        };

        // While error code is required, applet versions prior to update 1 don't include an error code, so only include the error code when it's provided
        if (errorCode) {
            eventOptions.ErrorCode = errorCode;
        }

        // Determine the telemetry type and event identifier
        var eventIdentifier;
        if (options.TelemetryProperties.TelemetryType === util.telemetry.Type.Exception) {
            eventIdentifier = "exception-telemetry-event";
        } else if (options.TelemetryProperties.TelemetryType === util.telemetry.Type.UserError) {
            eventIdentifier = "user-error-telemetry-event";
        } else if (options.TelemetryProperties.TelemetryType === util.telemetry.Type.SystemError) {
            eventIdentifier = "system-error-telemetry-event";
        } else if (options.TelemetryProperties.TelemetryType === util.telemetry.Type.OfficeError) {
            eventIdentifier = "office-error-telemetry-event";
        } else {
            eventIdentifier = "error-telemetry-event";
            options.TelemetryProperties.TelemetryType = util.telemetry.Type.Error;
        }

        // If telemetry details weren't provided, create the details out of the error message and optionally error details
        if (options.TelemetryProperties.TelemetryDetails === undefined) {
            options.TelemetryProperties.TelemetryDetails = options.Details === undefined ? message : resources.dynamicsApp.notification_WithDetailsMessageFormat(message, options.Details);
        }

        // Apply all telemetry properties to the telemetry event options
        for (var telemetryProperty in options.TelemetryProperties) {
            eventOptions[telemetryProperty] = options.TelemetryProperties[telemetryProperty];
        }

        // Log the telemetry event
        telemetryLogging.logEvent(eventIdentifier, eventOptions);
    }

    /** The qualifier format to use for IDs when an ID has a source provided. */
    const SourceQualifierFormat = "{0}-{1}";

    /**
     * Qualifies an ID by it's originating source's ID.
     * @param id The ID to qualify.
     * @param sourceID The ID of the originating source.
     * @returns The qualified ID if a source ID is provided, or the unmodified ID otherwise.
     */
    export function _qualifyBySourceID(id: string, sourceID?: string): string {
        return sourceID === undefined ? id : $format(SourceQualifierFormat, sourceID, id);
    }
}
